
export enum NotificationTypes {
    Alarm = "Alarm",
    EtaDeviation = "eta-deviation",
    NPS = "NPS",
    HeavyWeather = "heavyweather",
    ParametricRoll = "parametricroll",
    HeavyRolling = "heavyrolling",
    HighAccelerationForce = "highaccelerationforce",
    ETAAlerts = "eta-alerts",
    WniReports = "wni-reports",
    NoPlanPreEtd = "no_plan_pre_dept",
    WNIOriginalPlan = 'plan-type-original',
    ScheduledETAChange = "ScheduledETAChange",
    RequestETAChange = "RequestETAChange",
    ImminentRisk = "imminent-risk",
    ImminentRiskOnboard = "imminent-risk-onboard",
    Loadstar = "loadstar",
}

export enum AlarmTypes {
	Speed = "speed",
	Stoppage = "stoppage",
	Crosstrack = "crosstrack",
}

// NotificationResponseComposed is notification data containing JSON
export interface NotificationResponseComposed {
	id: string;
	notification_id: string;
	source: string;
	state: string;
	data: string;
	created_at: string;
    updatedAt?: string;
	category?: string;
}

// The responses are the 'notification_responses' array in the notification data
export interface NotificationResponse<T extends NotificationTypes> extends Omit<NotificationResponseComposed, "data"> {
  data: T extends keyof NotificationTypeToResponseData ? NotificationTypeToResponseData[T] : never; // parsed JSON
}

// NotificationTypeToResponseData is the nested data prop in each item the notification_responses array
export type NotificationTypeToResponseData = {
    [NotificationTypes.Alarm]: AlarmNotificationResponseData;
    [NotificationTypes.EtaDeviation]: EtaDeviationNotificationResponseData;
    [NotificationTypes.NPS]: NPSNotificationResponseData;
    [NotificationTypes.HeavyWeather]:HWANotificationResponseData ;
    [NotificationTypes.ParametricRoll]: ParametricRollNotificationResponseData;
    [NotificationTypes.HeavyRolling]: HeavyRollingNotificationResponseData;
    [NotificationTypes.HighAccelerationForce]: HighAccelerationForceNotificationResponseData;
    [NotificationTypes.ETAAlerts]: AlarmNotificationResponseData;
    [NotificationTypes.WniReports]: AlarmNotificationResponseData;
    [NotificationTypes.NoPlanPreEtd]: NoPlanPreEtdNotificationResponseData;
    [NotificationTypes.WNIOriginalPlan]: AlarmNotificationResponseData;
    [NotificationTypes.ScheduledETAChange]: ScheduledETAChangeNotificationData;
    [NotificationTypes.RequestETAChange]: RequestETAChangeNotificationData;
    [NotificationTypes.ImminentRisk]: ImminentRiskNotificationResponseData;
    [NotificationTypes.ImminentRiskOnboard]: ImminentRiskOnboardNotificationResponseData;
    [NotificationTypes.Loadstar]: LoadstarNotificationData;
};

export interface NotificationResponseDataBase {
	comments?: string|null;
}

export interface EtaDeviationNotificationResponseData extends NotificationResponseDataBase {
    reason?: string;
}

export interface NPSNotificationResponseData extends NotificationResponseDataBase {
    rating?: number;
}

export interface NoPlanPreEtdNotificationResponseData extends NotificationResponseDataBase {
    timeToEtd?: number;
    schedule_id?: string;
}
export interface HWANotificationResponseData extends NotificationResponseDataBase {
    endTs?: string;
}

export interface ParametricRollNotificationResponseData extends NotificationResponseDataBase {
        endTs?: string;
    }

export interface HeavyRollingNotificationResponseData extends NotificationResponseDataBase {
        endTs?: string;
    }

export interface HighAccelerationForceNotificationResponseData extends NotificationResponseDataBase {
        endTs?: string;
    }

export interface AlarmNotificationResponseData extends NotificationResponseDataBase {
    metadata?: AlarmMetaData;
    alarmType?: string;
    edited?: boolean;
	imo?: string;
	type?: string;
	startTs?: string;
	endTs?: string;
	alarmState?: string;
	mutedUntil?: string|null;
	audience?: number;
    id?: string;
    reason?: string;
}

export interface MetaData {
	planId?: number;
	voyageId: string;
}

export interface AlarmMetaData extends MetaData {
    offTrackDistance?: number;
    speedDiff?: number;
	planSource: string;
	requiredSpeed: number;
	startTs?: string;
    speed?: number;
}

// VoyNotificationComposed is notification data containing JSON
export interface VoyNotificationComposed {
	notification_responses: NotificationResponseComposed[];
	id: string;
	imo_no: number;
	category: string;
	type: string;
	data: string; // JSON
	source: string;
	created_at: string;
    updatedAt?: string;
	state: string;
}

// Override data prop from JSON to parsed javascript object.
// Using generics to ensure that the type of the data prop matches the type of the notification type
export interface VoyNotification<T extends NotificationTypes> extends Omit<VoyNotificationComposed, "data"|"notification_responses"|"type"> {
    data: T extends keyof NotificationTypeToData ? NotificationTypeToData[T] : never; // parsed JSON
    notification_responses: NotificationResponse<T>[];
    type: T;
}

type NotificationTypeToData = {
    [NotificationTypes.Alarm]: AlarmNotificationData;
    [NotificationTypes.EtaDeviation]: EtaDeviationNotificationData;
    [NotificationTypes.NPS]: NPSNotificationData;
    [NotificationTypes.ETAAlerts]: AlarmNotificationData;
    [NotificationTypes.HeavyWeather]: WeatherAlarmNotificationData;
    [NotificationTypes.ParametricRoll]: WeatherAlarmNotificationData;
    [NotificationTypes.HeavyRolling]: WeatherAlarmNotificationData;
    [NotificationTypes.HighAccelerationForce]: WeatherAlarmNotificationData;
    [NotificationTypes.NoPlanPreEtd] : NoPlanPreEtdNotificationData;
    [NotificationTypes.WniReports]: WniReportNotificationData;
    [NotificationTypes.ScheduledETAChange] : ScheduledETAChangeNotificationData;
    [NotificationTypes.RequestETAChange] : RequestETAChangeNotificationData;
    [NotificationTypes.ImminentRisk]: ImminentRiskNotificationResponseData;
    [NotificationTypes.ImminentRiskOnboard]: ImminentRiskOnboardNotificationResponseData;
    [NotificationTypes.Loadstar]: LoadstarNotificationData;
};

export interface NotificationDataBase {
    id: string|number;
	imo: number;
	audience: number;
    data?: string;
    state?: string;
}

export interface WniReportNotificationData extends NotificationDataBase {
    scheduleId: string;
}

export interface NPSNotificationData extends NotificationDataBase {
    rating: number;
    comments: string;
}

export interface NoPlanPreEtdNotificationData extends NotificationDataBase {
    timeToEtd: number;
    schedule_id: string;
}
export interface WeatherAlarmNotificationData extends NotificationDataBase {
    rating: number;
    comments: string;
    data: string
}

export interface EtaDeviationNotificationData extends NotificationDataBase {
    planId?: number;
	voyageId: string;
    etaDeviation: {
        waypointIndex: number;
        eta: string;
        delay: number;
    }
}

export interface AlarmNotificationData extends NotificationDataBase {
	alarmType: AlarmTypes;
    metadata: AlarmNotificationMetadata;
    startTs: string;
	endTs: string;
	alarmState: AlarmState;
	mutedUntil: string | null;
}

export interface ScheduledETAChangeNotificationData extends NotificationDataBase {
    plan_id: number;
    schedule_id: string;
    source: string;
    departure_terminal_code: string;
    arrival_terminal_code: string;
    arrival_portcall_id: string;
    departure_portcall_id: string;
    voyage_plan: ScheduleEntryData;
    schedule: ScheduleEntryData;
    deviation_reason: string;
    deviation_reason_code: string;
    deviation_reason_created_by: string;
    deviation_remark: string;
    deviation_remark_timestamp: string;
}

interface ScheduleEntryData{
    create_timestamp: string;
    arrivalTimings: TimingsData;
    departureTimings: TimingsData;
    bunker_consumption_mt: string;
    remaining_distance_nm: string;
    remaining_distance_pilot_nm: string;
    average_speed_nms: string;
    average_speed_pilot_nms: string;
    max_wind_speed_nms: string;
    max_wave_height_mts: string;
    max_swell_height_mts: string;
}

interface TimingsData{
    array: Timestamp[];
}

interface Timestamp{
    timestamp: string,
    type: string,
}

export interface RequestETAChangeNotificationData extends NotificationDataBase {
    plan_id: number;
    schedule_id: string;
    source: string;
    departure_terminal_code: string;
    arrival_terminal_code: string;
    arrival_portcall_id: string;
    departure_portcall_id: string;
    earliest_requested_eta: RequestedEntryData;
    scheduled: RequestedEntryData;
    deviation_reason: string;
    deviation_reason_code: string;
    deviation_reason_created_by: string;
    deviation_remark: string;
    deviation_remark_timestamp: string;
}

export interface LoadstarNotificationData extends NotificationDataBase {
    fore_draft: number;
    aft_draft: number;
    fluid_gm: number;
    solid_gm: number;
    live_reefers: number;
    max_roll_angle: number;
    schedule_id: string;
    voyage_number: string;
    departure_port: string;
    arrival_port: string;
    saved_at: string;
}


interface RequestedEntryData {
    arrivalTimings: RequestedTimingsData;
    departureTimings: RequestedTimingsData;
}

interface RequestedTimingsData {
    array: RequestedTimestamp[];
}

export interface RequestedTimestamp {
    createSource: { [key: string]: string };
    timestamp: string;
    type: string;
}

export interface ImminentRiskNotificationResponseData extends NotificationResponseDataBase {
    log_id: string;
}
export interface ImminentRiskOnboardNotificationResponseData extends NotificationResponseDataBase {
    hasOnboarded: boolean;
}

export enum AlarmState {
	Active = "Active",
	Ended = "Ended"
}

export interface AlarmNotificationMetadata {
	speed: number;
	planId: number;
	voyageId: string;
	speedDiff: number;
	planSource: string;
	passagePlan?: string;
	requiredSpeed: number;
}

export enum FeedbackTypes {
	VerySad = "Very sad",
	Sad = "Sad",
    Neutral = "Neutral",
    Happy = "Happy",
    VeryHappy = "Very Happy"
}

export enum WRMessageTypes {
    ReviewLater = "New Weather Advice has been snoozed for 1 hour.",
    Accept = "Weather Routing advice accepted. The approved route has been submitted to WNI.",
    Reject = "You have submitted your counter proposal to WNI and awaiting advice from WNI.",
    UpdateRequest = "You have successfully requested Manual Update Request."
}

export const mockVoyEtaDeviationNotificationResponse: VoyNotificationComposed[] = [
    {
        "state": "Ended",
        "notification_responses": [
            {
                "id": "eac36aa7-5195-40d3-bd7f-1d8044e64c0a",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reasons\":\"Following weather routing advice\",\"comment\":\"asdasd\"}",
                "created_at": "2023-06-02T10:52:11.672907"
            },
            {
                "id": "eac36aa7-5195-40d3-bd7f-1d8044e64c0a",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Ended",
                "data": "{\"reasons\":\"Ended by system\",\"comment\":\"\"}",
                "created_at": "2023-06-02T10:52:11.672907"
            },
        ],
        "id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "eta-deviation",
        "data": "{\"id\":361253,\"DeviationDuration\":10,\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"metadata\":{\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8qnt9-8qnta\"}}",
        "created_at": "2024-26-01T16:00:08.991149"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "eac36aa7-5195-40d3-bd7f-1d8044e64c0a",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Shore",
                "state": "Active",
                "data": "{\"reasons\":\"Following weather routing advice\",\"comment\":\"asdasd\"}",
                "created_at": "2023-06-02T10:52:11.672907"
            },
        ],
        "id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "eta-deviation",
        "data": "{\"id\":361253,\"imo\":2001193,\"DeviationDuration\":10,\"alarmType\":\"crosstrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8qnt9-8qnta\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2024-26-01T16:00:08.991149"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "eac36aa7-5195-40d3-bd7f-1d8044e64c0a",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Shore",
                "state": "Active",
                "data": "{\"reasons\":\"Following weather routing advice\",\"comment\":\"asdasd\"}",
                "created_at": "2023-06-02T10:52:11.672907"
            },
        ],
        "id": "76d08259-4450-4ffd-8860-cb2bdc667f7211",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "eta-deviation",
        "data": "{\"id\":361253,\"imo\":2001193,\"DeviationDuration\":10,\"alarmType\":\"crosstrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8qnt9-8qnta\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2024-26-01T16:00:08.991149"
    },
];

export const mockVoyAlarmNotificationFinal: VoyNotificationComposed[] = [
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "eac36aa7-5195-40d3-bd7f-1d8044e64c0a",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Shore",
                "state": "Active",
                "data": "{\"reasons\":\"Following weather routing advice\",\"comment\":\"asdasd\"}",
                "created_at": "2023-06-02T10:52:11.672907"
            },
            {
                "id": "8cc83deb-7291-470a-828d-d2fd680971d0",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-10T17:10:59.891944"
            },
            {
                "id": "e8e0fa51-41a8-4d5c-a8f8-787cafbfb161",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-10T17:26:28.633999"
            },
            {
                "id": "1d5d25b5-0930-4a58-a0c1-09c6bfb84335",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-10T17:41:09.012667"
            },
            {
                "id": "6d7e629d-ca89-4b0d-8756-f9eabea2fea4",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-11T11:27:24.661243"
            },
            {
                "id": "f30103db-bf47-4b04-a920-1a11a8ec6868",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-11T11:34:44.039366"
            },
            {
                "id": "565a6974-ed8c-4459-a109-f34b691d4302",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-11T11:36:18.233327"
            },
            {
                "id": "a86b5f67-6eec-4176-a92f-ebdbffbe697d",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T06:07:37.949782"
            },
            {
                "id": "daab8c62-773c-4fce-8060-bd6b7279c603",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T06:10:50.813583"
            }
        ],
        "id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "eta-deviation",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"crosstrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-02T10:51:08.991149"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "eac36aa7-5195-40d3-bd7f-1d8044e64c0a",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Shore",
                "state": "Active",
                "data": "{\"reasons\":\"Following weather routing advice\",\"comment\":\"asdasd\"}",
                "created_at": "2023-06-02T10:52:11.672907"
            },
            {
                "id": "8cc83deb-7291-470a-828d-d2fd680971d0",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-10T17:10:59.891944"
            },
            {
                "id": "e8e0fa51-41a8-4d5c-a8f8-787cafbfb161",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-10T17:26:28.633999"
            },
            {
                "id": "1d5d25b5-0930-4a58-a0c1-09c6bfb84335",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-10T17:41:09.012667"
            },
            {
                "id": "6d7e629d-ca89-4b0d-8756-f9eabea2fea4",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-11T11:27:24.661243"
            },
            {
                "id": "f30103db-bf47-4b04-a920-1a11a8ec6868",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-11T11:34:44.039366"
            },
            {
                "id": "565a6974-ed8c-4459-a109-f34b691d4302",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-11T11:36:18.233327"
            },
            {
                "id": "a86b5f67-6eec-4176-a92f-ebdbffbe697d",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T06:07:37.949782"
            },
            {
                "id": "daab8c62-773c-4fce-8060-bd6b7279c603",
                "notification_id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T06:10:50.813583"
            }
        ],
        "id": "76d08259-4450-4ffd-8860-cb2bdc667f72",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"crosstrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-02T10:51:08.991149"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "5b5ab9d3-bb52-4a89-b740-101786815f38",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reasons\":\"Awaiting min. safe speed for ETA\",\"comment\":\"null\"}",
                "created_at": "2023-06-03T07:18:21.437325"
            },
            {
                "id": "26cebf68-dfe5-42dc-bbf6-52a3ddbeef4e",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-09T05:23:45.369689"
            },
            {
                "id": "d73ddb60-384c-4a70-a7d0-2c49b6c1b9b6",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Anchorage/drifting awaiting berth\",\"comments\":null}",
                "created_at": "2023-06-09T10:34:13.435456"
            },
            {
                "id": "1b955060-521b-49c1-8d42-c33af6f971bb",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Contingency\",\"comments\":null}",
                "created_at": "2023-06-10T17:12:42.783978"
            },
            {
                "id": "f0cd552b-2893-47ca-8461-0d8786653a6d",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\"n ,\"}",
                "created_at": "2023-06-10T17:13:55.709628"
            },
            {
                "id": "69578312-4f63-4a7b-b226-839a0e22dc59",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Awaiting min. safe speed for ETA\",\"comments\":null}",
                "created_at": "2023-06-10T17:14:40.793194"
            },
            {
                "id": "6d1fff4c-c0ee-4b9c-83aa-1faead8b5341",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-10T17:17:10.422526"
            },
            {
                "id": "db3c1f43-d5a0-4597-a4e0-cdbc9b3f5ae9",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-10T17:22:44.894357"
            },
            {
                "id": "d09fdb4c-36dd-413a-bcd4-84fff77eccc9",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-10T17:23:27.605078"
            },
            {
                "id": "2eb651a4-b3f7-4c3a-84d9-f4555d00cb4c",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\"m\"}",
                "created_at": "2023-06-10T17:23:30.058353"
            },
            {
                "id": "3a93cf01-72dc-4dc7-8a0c-4a88c1de35a0",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-10T17:46:31.44324"
            },
            {
                "id": "c34ac7bd-1001-4f78-b928-0dfcbeac8bc5",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\",\"}",
                "created_at": "2023-06-10T17:46:39.569245"
            },
            {
                "id": "af50b8ea-aeea-466c-9689-6386d5ca942d",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Awaiting min. safe speed for ETA\",\"comments\":null}",
                "created_at": "2023-06-11T05:42:20.096329"
            },
            {
                "id": "32456ea0-77ac-42d5-a2eb-7598f71d7293",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-11T11:35:24.590869"
            },
            {
                "id": "87e57d83-4147-4fb0-8863-fb6b15e79aa5",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\"n\"}",
                "created_at": "2023-06-11T11:35:26.265968"
            },
            {
                "id": "7b5f2a80-4c94-4d8a-b3f2-fef7c2616321",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-12T06:10:43.646613"
            },
            {
                "id": "81943afe-72ad-4b30-9df8-88b2436892ba",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\"n,\"}",
                "created_at": "2023-06-12T06:10:45.855596"
            },
            {
                "id": "db1a98db-e457-4d2d-94dc-92f67f3072c4",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T11:14:01.654932"
            },
            {
                "id": "86ef142f-3c78-4f1f-a4ae-a401f5189d8e",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Awaiting min. safe speed for ETA\",\"comments\":null}",
                "created_at": "2023-06-13T06:59:43.64487"
            },
            {
                "id": "037ae618-faa7-4550-82cb-f4edae7a5ffa",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Awaiting min. safe speed for ETA\",\"comments\":null}",
                "created_at": "2023-06-13T11:15:56.697062"
            },
            {
                "id": "e6cb71f8-4bb0-45ee-ad18-098c6b2468fa",
                "notification_id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Anchorage/drifting awaiting berth\",\"comments\":null}",
                "created_at": "2023-06-13T11:16:09.985948"
            }
        ],
        "id": "df3dec16-fcd0-4462-b4c8-7a74dd637816",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"stoppage\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-02T10:50:52.973724"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "a7398901-4799-46e1-a9e0-ac7a105f3dd6",
                "notification_id": "454fcb42-f1b5-4a85-b5ca-ea515420dc2e",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reasons\":\"Affected by heavy weather\",\"comment\":\"null\"}",
                "created_at": "2023-06-03T07:32:38.373164"
            },
            {
                "id": "b5a62a22-5f52-469b-8a95-f881a15d015f",
                "notification_id": "454fcb42-f1b5-4a85-b5ca-ea515420dc2e",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"High speed - Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-09T05:24:30.241355"
            },
            {
                "id": "445c0633-df7d-4e0b-bd73-235988ab3646",
                "notification_id": "454fcb42-f1b5-4a85-b5ca-ea515420dc2e",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid critical RPM\",\"comments\":null}",
                "created_at": "2023-06-10T17:17:04.258915"
            },
            {
                "id": "4b6acab9-16b7-4571-97d8-1810566fbf35",
                "notification_id": "454fcb42-f1b5-4a85-b5ca-ea515420dc2e",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Proceeding below 10% MCR\",\"comments\":null}",
                "created_at": "2023-06-11T11:35:19.916926"
            }
        ],
        "id": "454fcb42-f1b5-4a85-b5ca-ea515420dc2e",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-02T10:51:02.422667"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "c1a6a8c6-ca65-4cc5-947b-ddbde73c3338",
                "notification_id": "5e4e29e4-6c18-4d29-b99e-4dde05ccfd3c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reasons\":\"Avoid heavy weather\",\"comment\":\"null\"}",
                "created_at": "2023-06-05T05:48:33.52786"
            },
            {
                "id": "af1bb0a4-a725-4e58-ad6b-7e62f01b4a9d",
                "notification_id": "5e4e29e4-6c18-4d29-b99e-4dde05ccfd3c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for stoppage\",\"comments\":null}",
                "created_at": "2023-06-10T17:16:32.569505"
            },
            {
                "id": "f3958d24-b525-40b5-9ab1-cc9a35fa416f",
                "notification_id": "5e4e29e4-6c18-4d29-b99e-4dde05ccfd3c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-10T17:16:34.282105"
            },
            {
                "id": "24ffea7c-d641-4aa6-aae8-065c4a9e2aa7",
                "notification_id": "5e4e29e4-6c18-4d29-b99e-4dde05ccfd3c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-10T17:42:11.764899"
            },
            {
                "id": "8b4dfa5a-aa41-4166-bb47-1b54d2d4ee4a",
                "notification_id": "5e4e29e4-6c18-4d29-b99e-4dde05ccfd3c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\"Gg\"}",
                "created_at": "2023-06-12T16:38:51.228073"
            },
            {
                "id": "23ff4931-63dd-4f3b-8856-98ca747ac5ee",
                "notification_id": "5e4e29e4-6c18-4d29-b99e-4dde05ccfd3c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Skipping one or more waypoints\",\"comments\":null}",
                "created_at": "2023-06-13T06:28:17.475461"
            }
        ],
        "id": "5e4e29e4-6c18-4d29-b99e-4dde05ccfd3c",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-05T05:46:30.803662"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "531a22e7-0f95-4f90-880d-e4b8fa1cb9c3",
                "notification_id": "e68a61f2-b4bd-4331-a781-a70d3e32db4e",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reasons\":\"Avoid heavy weather\",\"comment\":\"null\"}",
                "created_at": "2023-06-05T05:50:25.11278"
            },
            {
                "id": "b9e05adf-9b24-475f-aa4e-53507f87a1db",
                "notification_id": "e68a61f2-b4bd-4331-a781-a70d3e32db4e",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-10T17:12:30.354937"
            }
        ],
        "id": "e68a61f2-b4bd-4331-a781-a70d3e32db4e",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-05T05:49:43.165011"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "cfa8d583-619b-42b3-8085-052dc97fe7e2",
                "notification_id": "4fc82193-9ff4-4066-954c-3a00d57ccfa5",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reasons\":\"Low speed - Following weather routing advice\",\"comment\":\"null\"}",
                "created_at": "2023-06-05T05:51:29.752892"
            },
            {
                "id": "43d59e4d-2957-4700-98b0-aa3b3fe69a2d",
                "notification_id": "4fc82193-9ff4-4066-954c-3a00d57ccfa5",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid critical RPM\",\"comments\":null}",
                "created_at": "2023-06-09T10:34:30.67542"
            }
        ],
        "id": "4fc82193-9ff4-4066-954c-3a00d57ccfa5",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-05T05:51:05.605295"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "d527b24c-6a7e-4ec5-8e46-9eb7e8d71e55",
                "notification_id": "3242cca6-0035-4ada-b924-9673343227e6",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reasons\":\"Dual speed voyage\",\"comment\":\"Test for speed\"}",
                "created_at": "2023-06-05T05:53:30.325709"
            },
            {
                "id": "1845ec7e-cdd1-41b4-9873-a8c7f936b82e",
                "notification_id": "3242cca6-0035-4ada-b924-9673343227e6",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid critical RPM\",\"comments\":null}",
                "created_at": "2023-06-12T16:40:22.665886"
            }
        ],
        "id": "3242cca6-0035-4ada-b924-9673343227e6",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-05T05:52:07.344799"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "bea9e6ca-8ebe-4226-a666-66043590a234",
                "notification_id": "900c07a4-2c38-473f-aed8-b4c7b81705c1",
                "source": "Vessel",
                "state": "active",
                "data": "{}",
                "created_at": "2023-06-06T06:26:44.365319"
            },
            {
                "id": "eb7e4523-fb12-411d-9ca1-d9d2808012a7",
                "notification_id": "900c07a4-2c38-473f-aed8-b4c7b81705c1",
                "source": "Vessel",
                "state": "active",
                "data": "{\"reasons\":\"test\",\"comment\":\"null\"}",
                "created_at": "2023-06-06T06:35:09.089136"
            },
            {
                "id": "f8c8f9d4-bc2c-43f0-9e6b-fb65389b2e1c",
                "notification_id": "900c07a4-2c38-473f-aed8-b4c7b81705c1",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-07T13:03:21.90388"
            },
            {
                "id": "58639942-69fb-4345-8bdf-5c438c58197d",
                "notification_id": "900c07a4-2c38-473f-aed8-b4c7b81705c1",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-09T10:59:18.663387"
            }
        ],
        "id": "900c07a4-2c38-473f-aed8-b4c7b81705c1",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-05T05:50:07.473482"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "e558bf97-1952-4b39-ba16-438bf1627555",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"High speed - Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-06T10:41:18.61924"
            },
            {
                "id": "ed0b7bb3-5d74-48ea-8521-c563be35d8fb",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid critical RPM\",\"comments\":null}",
                "created_at": "2023-06-06T11:06:23.664589"
            },
            {
                "id": "994922ae-05e1-4b25-9abc-9d0c3ff9c93e",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Low speed - Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-07T06:49:51.777761"
            },
            {
                "id": "1fab6712-4cff-4b4f-bf64-59d89dcaa0f3",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Proceeding below 10% MCR\",\"comments\":\"test\"}",
                "created_at": "2023-06-07T13:05:34.736232"
            },
            {
                "id": "9c87b905-9128-47f5-9f82-8c95b0727031",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-08T07:59:36.73788"
            },
            {
                "id": "b938d570-1b90-4ed8-b46f-1001d7189c22",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-09T05:24:05.542765"
            },
            {
                "id": "431a6f1b-b141-4cb9-93ec-a4b00b1911a9",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Low speed - Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-09T05:25:57.973159"
            },
            {
                "id": "c0f5965f-6ad3-4c26-81fd-ea3f8aaa84ed",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Current/weather different than forecasted\",\"comments\":null}",
                "created_at": "2023-06-09T10:34:24.78774"
            },
            {
                "id": "d1fc6626-9db6-4dc4-a095-efd6e298c46d",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for stoppage\",\"comments\":null}",
                "created_at": "2023-06-09T10:59:06.237984"
            },
            {
                "id": "e6123e46-dee3-48dc-a7e1-3c84cfd549a7",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"High speed - Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-09T10:59:08.350106"
            },
            {
                "id": "2876a883-4ed1-4cab-8e6e-18fdfd078354",
                "notification_id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Proceeding below 10% MCR\",\"comments\":null}",
                "created_at": "2023-06-10T17:45:35.673108"
            }
        ],
        "id": "2949a412-1d47-4a7f-ae3c-95ab2b1c68c0",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-05T06:16:41.432469"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "e454f74e-932f-40bb-af9c-20da25381492",
                "notification_id": "ebdc13c3-e2a8-401f-9ee6-960e31a29851",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-08T12:12:44.065347"
            },
            {
                "id": "4623e89a-ef0e-4ad9-bdf8-f0d43ec5ff64",
                "notification_id": "ebdc13c3-e2a8-401f-9ee6-960e31a29851",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-08T12:13:49.693404"
            },
            {
                "id": "839ce69c-7ff2-4337-a3ac-fcb575478ecc",
                "notification_id": "ebdc13c3-e2a8-401f-9ee6-960e31a29851",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-09T05:31:03.668067"
            },
            {
                "id": "92fc6e2f-4686-4221-9a92-f2b2884d0ca1",
                "notification_id": "ebdc13c3-e2a8-401f-9ee6-960e31a29851",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-09T05:31:28.815821"
            },
            {
                "id": "33622b76-0e05-4983-a18f-8d129e4911fa",
                "notification_id": "ebdc13c3-e2a8-401f-9ee6-960e31a29851",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-09T10:34:18.388472"
            },
            {
                "id": "cba6dab6-650a-423d-8dd3-ecb9e99f553e",
                "notification_id": "ebdc13c3-e2a8-401f-9ee6-960e31a29851",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-13T06:33:10.376395"
            }
        ],
        "id": "ebdc13c3-e2a8-401f-9ee6-960e31a29851",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"stoppage\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-07T13:23:42.664512"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "0cdc14a9-e47f-4b45-8df2-c274fa9d799d",
                "notification_id": "a391af10-da5c-493d-a4d3-e94f22d953d0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Other\",\"comments\":null}",
                "created_at": "2023-06-08T12:13:56.851837"
            },
            {
                "id": "40533e7a-8a8b-4941-828c-83d896fbd345",
                "notification_id": "a391af10-da5c-493d-a4d3-e94f22d953d0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Other\",\"comments\":null}",
                "created_at": "2023-06-08T12:13:58.448162"
            },
            {
                "id": "c5fa94ab-2f54-4e4c-9783-9db2eed6f406",
                "notification_id": "a391af10-da5c-493d-a4d3-e94f22d953d0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-10T17:47:07.609286"
            },
            {
                "id": "cf862dd9-0b2d-48da-b6e8-1c42b858c087",
                "notification_id": "a391af10-da5c-493d-a4d3-e94f22d953d0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T11:25:48.559591"
            },
            {
                "id": "bfb9cd66-4ec8-4837-9340-73224265fc32",
                "notification_id": "a391af10-da5c-493d-a4d3-e94f22d953d0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-12T16:38:48.605271"
            },
            {
                "id": "c7ae2d5b-f361-4bd1-a358-e74d0421730d",
                "notification_id": "a391af10-da5c-493d-a4d3-e94f22d953d0",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Awaiting min. safe speed for ETA\",\"comments\":null}",
                "created_at": "2023-06-12T16:39:04.659664"
            }
        ],
        "id": "a391af10-da5c-493d-a4d3-e94f22d953d0",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"stoppage\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-08T12:13:04.513377"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "7ee3a4a4-b54e-42f5-a1f1-b8742cf06308",
                "notification_id": "983f1442-21f0-497f-8c3e-69cb55c01f28",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Anchorage/drifting awaiting berth\",\"comments\":null}",
                "created_at": "2023-06-08T12:14:29.061727"
            },
            {
                "id": "98eb9aff-1504-4423-939c-4dbf12a2036e",
                "notification_id": "983f1442-21f0-497f-8c3e-69cb55c01f28",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-10T17:12:51.137604"
            },
            {
                "id": "e402aaee-5086-40d5-b525-0cd2b8300234",
                "notification_id": "983f1442-21f0-497f-8c3e-69cb55c01f28",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Awaiting min. safe speed for ETA\",\"comments\":null}",
                "created_at": "2023-06-10T17:35:36.099025"
            }
        ],
        "id": "983f1442-21f0-497f-8c3e-69cb55c01f28",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"stoppage\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-08T12:14:13.989009"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "3ceb9c53-cd53-4040-aaf5-3beb1ee1b725",
                "notification_id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-08T12:14:33.125314"
            },
            {
                "id": "b5c1fd5b-e0ca-41db-a571-ee69cc749a65",
                "notification_id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Awaiting min. safe speed for ETA\",\"comments\":null}",
                "created_at": "2023-06-10T17:12:37.751289"
            },
            {
                "id": "31e882a5-5b34-43a6-8ffb-54ff49abb1e0",
                "notification_id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-10T17:13:45.655407"
            },
            {
                "id": "537a1ac7-cf48-4b10-8a1c-0500fbb2f62a",
                "notification_id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-10T17:13:47.009313"
            },
            {
                "id": "e0c8c665-4e1c-42f4-a340-2ccf7c610cf8",
                "notification_id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\"k\"}",
                "created_at": "2023-06-10T17:13:49.35058"
            },
            {
                "id": "bb180016-ebf7-4a0f-b63b-1f7a926e4a7f",
                "notification_id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":null}",
                "created_at": "2023-06-10T17:32:20.032882"
            },
            {
                "id": "692f3dfd-b948-4e8a-963b-94421d19202a",
                "notification_id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\"zx\"}",
                "created_at": "2023-06-10T17:32:36.846719"
            },
            {
                "id": "65ad847c-2267-4716-ba16-ac0be7227df1",
                "notification_id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Contingency\",\"comments\":null}",
                "created_at": "2023-06-10T17:48:06.797012"
            }
        ],
        "id": "ae16941f-b035-4f5e-99e7-68f0a3e9ae06",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"stoppage\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-08T12:14:12.932517"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "eb1cc07e-a529-4666-8a94-76cd7943f32e",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-10T17:30:14.239266"
            },
            {
                "id": "0c87fd41-eb7f-45e9-9d69-a0ab42e08268",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-10T17:49:10.772519"
            },
            {
                "id": "1e0fe584-ded8-42e8-a055-a9a9659dae5f",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-12T06:10:38.227609"
            },
            {
                "id": "8c3c3b32-ce9e-4708-84da-33ebb52316e0",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Following weather routing advice\",\"comments\":null}",
                "created_at": "2023-06-12T11:14:25.436114"
            },
            {
                "id": "8d32d4ef-6b00-464e-85f0-4cd02283a261",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-12T11:16:35.144437"
            },
            {
                "id": "a322def2-d472-4fff-ac61-2d2ff57df0c6",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Dense traffic\",\"comments\":null}",
                "created_at": "2023-06-12T11:18:54.297516"
            },
            {
                "id": "46267609-7492-4686-b360-f82ec6b62de5",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-12T11:22:29.29802"
            },
            {
                "id": "f185c95c-3406-4ef6-b4e7-36d9df11249d",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-12T11:22:54.679416"
            },
            {
                "id": "785deaa8-928f-47e2-964a-6116dfcba14f",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-12T11:24:00.270328"
            },
            {
                "id": "6f82c150-71dc-4b55-83ae-651cde19026c",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-12T11:25:58.884613"
            },
            {
                "id": "c88905f3-56f0-4fe6-8e2e-566f3c046eec",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T11:27:34.65686"
            },
            {
                "id": "23eecfa6-9bb6-44dc-8700-08535350c02d",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-12T11:28:45.385287"
            },
            {
                "id": "0e2e04de-b131-4463-908d-b6461ef85038",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-12T16:38:25.610862"
            },
            {
                "id": "463fa287-6e99-4c02-94ee-32856b698c1d",
                "notification_id": "410ab18a-7af0-4600-848b-d65651d65038",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Skipping one or more waypoints\",\"comments\":null}",
                "created_at": "2023-06-12T16:45:18.952408"
            }
        ],
        "id": "410ab18a-7af0-4600-848b-d65651d65038",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-10T17:29:47.500794"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "5dd475b8-05dc-4301-b1a7-9ea46d78808f",
                "notification_id": "f442b9d8-ce68-4a2f-81d5-d94ce5729512",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-12T11:33:56.350328"
            },
            {
                "id": "d6a9b2f5-fc91-42f3-8cb8-22c0cf8bcdb5",
                "notification_id": "f442b9d8-ce68-4a2f-81d5-d94ce5729512",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T16:42:44.01949"
            },
            {
                "id": "854965e6-a613-4e91-acfe-3cdf3f8d44d7",
                "notification_id": "f442b9d8-ce68-4a2f-81d5-d94ce5729512",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-12T16:44:54.046732"
            },
            {
                "id": "684e9cc9-69d9-441a-879a-da2d83b6264f",
                "notification_id": "f442b9d8-ce68-4a2f-81d5-d94ce5729512",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-12T16:47:25.314799"
            },
            {
                "id": "8344276b-d962-4c42-a4ce-f669a702e8ed",
                "notification_id": "f442b9d8-ce68-4a2f-81d5-d94ce5729512",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-13T05:37:03.073826"
            },
            {
                "id": "9bffed52-1eb1-4407-acf8-47f2d87a66c7",
                "notification_id": "f442b9d8-ce68-4a2f-81d5-d94ce5729512",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-13T06:00:58.553542"
            },
            {
                "id": "03f75bb9-81eb-4b00-945d-9c7445c9e0ce",
                "notification_id": "f442b9d8-ce68-4a2f-81d5-d94ce5729512",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-13T06:33:40.452972"
            }
        ],
        "id": "f442b9d8-ce68-4a2f-81d5-d94ce5729512",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-12T10:07:58.728015"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "fcc9b343-f98f-4bcd-9932-543a3ad538a6",
                "notification_id": "81855704-1537-49a5-817b-d511d3177827",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Awaiting min. safe speed for ETA\",\"comments\":null}",
                "created_at": "2023-06-13T05:21:07.515395"
            },
            {
                "id": "f3238da8-37b3-4bbd-8468-80622bcdd0c9",
                "notification_id": "81855704-1537-49a5-817b-d511d3177827",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-13T05:37:06.751793"
            },
            {
                "id": "272590c3-fd50-4f8c-a7a1-c81c549d5a00",
                "notification_id": "81855704-1537-49a5-817b-d511d3177827",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Technical issue\",\"comments\":\"dsfd\"}",
                "created_at": "2023-06-13T06:28:24.235355"
            },
            {
                "id": "fa8480a1-aa01-4ae5-ac1d-3e5e0c60eca7",
                "notification_id": "81855704-1537-49a5-817b-d511d3177827",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-13T06:35:59.889329"
            },
            {
                "id": "71a3a2ee-7f9b-4e5d-b5ad-f6740e4a2540",
                "notification_id": "81855704-1537-49a5-817b-d511d3177827",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-13T06:59:55.627542"
            },
            {
                "id": "cdb39486-0f2a-402e-976a-1822e57f10ff",
                "notification_id": "81855704-1537-49a5-817b-d511d3177827",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-13T08:22:19.512041"
            }
        ],
        "id": "81855704-1537-49a5-817b-d511d3177827",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"stoppage\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T04:32:35.758651"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "8b40d047-16be-4da1-9c2d-6eb9d10f34ed",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-13T05:37:10.93377"
            },
            {
                "id": "fd4e68fa-a0c7-463f-8037-dc39c23cc1bc",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Proceeding below 10% MCR\",\"comments\":null}",
                "created_at": "2023-06-13T06:25:05.484338"
            },
            {
                "id": "32dda746-98b7-4331-98ab-e6545d3f1f8c",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Proceeding below 10% MCR\",\"comments\":null}",
                "created_at": "2023-06-13T06:33:36.492349"
            },
            {
                "id": "13c3c931-c402-4e9e-8496-42bd6be990a4",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Proceeding below 10% MCR\",\"comments\":null}",
                "created_at": "2023-06-13T06:36:14.046161"
            },
            {
                "id": "bcb43531-02fe-46dc-b9a5-f98e776c2af9",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-13T06:59:51.316507"
            },
            {
                "id": "615ec8dc-c005-49e9-aa7b-3c4d2e5c0e6c",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for stoppage\",\"comments\":null}",
                "created_at": "2023-06-13T08:22:37.388884"
            },
            {
                "id": "b07c2eba-cc4d-447c-8259-dc9525d3f55f",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for stoppage\",\"comments\":null}",
                "created_at": "2023-06-13T08:22:42.965231"
            },
            {
                "id": "1a15d9c5-c334-4742-a86e-3e615bf247c5",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for stoppage\",\"comments\":null}",
                "created_at": "2023-06-13T08:22:45.183608"
            },
            {
                "id": "369622ab-c60c-4eda-a6a3-d750bc4a7ae2",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for stoppage\",\"comments\":null}",
                "created_at": "2023-06-13T08:22:53.462997"
            },
            {
                "id": "7ab33687-825c-4dd7-ab1a-d29dfdd6826f",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for stoppage\",\"comments\":null}",
                "created_at": "2023-06-13T08:23:04.900703"
            },
            {
                "id": "3126b836-adff-48ec-91cd-27086300cba6",
                "notification_id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-13T08:26:15.195148"
            }
        ],
        "id": "7599ec6e-00c2-4df8-8d92-8d077c22c8cb",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T04:31:48.659832"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "cb8e0547-08b1-48b7-9cd9-793e4a70f510",
                "notification_id": "e7b1ed35-55cf-4eba-92e4-f165c42a1340",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-13T06:59:59.768522"
            }
        ],
        "id": "e7b1ed35-55cf-4eba-92e4-f165c42a1340",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T06:59:06.058969"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "36ecda40-be24-405b-af93-80c6b18ff27c",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for stoppage\",\"comments\":null}",
                "created_at": "2023-06-13T08:47:03.069509"
            },
            {
                "id": "e363cece-5753-4a15-b3d1-edbc0b74dd31",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T08:56:10.245757"
            },
            {
                "id": "ebb26dd2-90d3-43b4-aa20-8545ec7e6289",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T08:56:11.036181"
            },
            {
                "id": "929d5fd6-b910-496f-8707-af474e125969",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T08:56:15.856486"
            },
            {
                "id": "a07c41ff-9078-466f-87c6-9da78c674131",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T08:56:17.062647"
            },
            {
                "id": "f6a891d5-1769-4ae7-89f3-f6c894928b15",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T08:56:17.689191"
            },
            {
                "id": "ad28d920-50c0-43ce-86e2-8c928f7bb07a",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T08:56:18.545799"
            },
            {
                "id": "6299c400-a4ff-45b9-8f4e-853a9aedef84",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T08:56:33.760752"
            },
            {
                "id": "fb20d0c5-9c75-47d4-8d14-2f5001f06cea",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T08:56:39.301591"
            },
            {
                "id": "2244c5b0-4a84-42cf-a62f-908a9e77276c",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-13T09:08:58.568402"
            },
            {
                "id": "97645d74-7ea5-49b3-b981-87f99dc8a5f8",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T09:09:29.510659"
            },
            {
                "id": "ab4e56cc-9395-4525-9fae-1481ff1425f4",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Select reason for offtrack\",\"comments\":null}",
                "created_at": "2023-06-13T09:09:32.415372"
            },
            {
                "id": "c1e33bc9-bb75-441c-997e-3c995f0689ca",
                "notification_id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-13T10:06:27.045418"
            }
        ],
        "id": "2db0f49c-9d22-4a36-839b-8c4edeafac0c",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T08:45:16.054548"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "766a6020-489d-434b-ac59-0287cf770ad6",
                "notification_id": "ddb582a5-b5e8-4ba1-b00a-39095707d848",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-13T09:09:06.02158"
            }
        ],
        "id": "ddb582a5-b5e8-4ba1-b00a-39095707d848",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T08:45:07.039212"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "7f6a0fa7-99ef-487a-bf1a-58b10912a92d",
                "notification_id": "97fd8ade-220d-437f-a9c4-d0a2e3075835",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-13T09:59:41.91622"
            }
        ],
        "id": "97fd8ade-220d-437f-a9c4-d0a2e3075835",
        "imo_no": 2001193,
        "source": "Shore",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"crosstrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T09:25:44.076337"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "62064409-831c-41f1-afb6-9938b154b730",
                "notification_id": "b7dc329f-b984-4e49-80d8-6a155f1cc479",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-13T09:59:45.843072"
            }
        ],
        "id": "b7dc329f-b984-4e49-80d8-6a155f1cc479",
        "imo_no": 2001193,
        "source": "Shore",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361258,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T09:33:32.105201"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "8ea16806-fecf-4850-a1d5-a5d889158880",
                "notification_id": "58923507-ec81-43c4-ba64-056fb6f44dc6",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Contingency\",\"comments\":null}",
                "created_at": "2023-06-13T09:59:50.143432"
            }
        ],
        "id": "58923507-ec81-43c4-ba64-056fb6f44dc6",
        "imo_no": 2001193,
        "source": "Shore",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T09:33:22.750419"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "72e6be6f-6afc-41a9-85d8-c2b4f0d512de",
                "notification_id": "58b20ae4-d2d2-475c-9636-7634d6c31dc5",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Affected by heavy weather\",\"comments\":null}",
                "created_at": "2023-06-13T09:59:54.299277"
            }
        ],
        "id": "58b20ae4-d2d2-475c-9636-7634d6c31dc5",
        "imo_no": 2001193,
        "source": "Shore",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"speed\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T09:52:47.216111"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "8b400913-8c73-4cd3-a41f-0bccc8c5f3f1",
                "notification_id": "b2a086be-f177-431f-874e-5cf6f8cb4fbc",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-13T09:59:58.254215"
            }
        ],
        "id": "b2a086be-f177-431f-874e-5cf6f8cb4fbc",
        "imo_no": 2001193,
        "source": "Shore",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361259,\"imo\":2001193,\"alarmType\":\"stoppage\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T09:54:01.475864"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "d538ea90-708a-4a47-b0d6-840932c28f21",
                "notification_id": "5fd96afc-d0cb-4c0b-8b75-3f8b4bf72376",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-13T10:06:32.763502"
            }
        ],
        "id": "5fd96afc-d0cb-4c0b-8b75-3f8b4bf72376",
        "imo_no": 2001193,
        "source": "Shore",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T09:54:45.968349"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "c941f881-2254-415e-92ed-78a751381d6b",
                "notification_id": "a321cb39-b333-4436-8f82-f23ce27de20f",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-13T10:06:36.799469"
            }
        ],
        "id": "a321cb39-b333-4436-8f82-f23ce27de20f",
        "imo_no": 2001193,
        "source": "Shore",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361259,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T09:54:40.905535"
    },
    {
        "state": "Active",
        "notification_responses": [
            {
                "id": "68d3dc77-5164-4c17-8fb9-65c6a4e46556",
                "notification_id": "eb9b6975-c5a4-4659-921a-2d53cc93a028",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Change of destination\",\"comments\":null}",
                "created_at": "2023-06-13T11:06:21.103054"
            },
            {
                "id": "97a3d784-ca1a-4748-b122-de0645acc740",
                "notification_id": "eb9b6975-c5a4-4659-921a-2d53cc93a028",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Route revised\",\"comments\":null}",
                "created_at": "2023-06-13T11:12:10.372235"
            },
            {
                "id": "4b68998f-8bd0-43f2-bc95-42a850d82171",
                "notification_id": "eb9b6975-c5a4-4659-921a-2d53cc93a028",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Avoid heavy weather\",\"comments\":null}",
                "created_at": "2023-06-13T11:15:42.175275"
            },
            {
                "id": "d8f86814-e09d-48c9-8ac2-bd44ca8a5759",
                "notification_id": "eb9b6975-c5a4-4659-921a-2d53cc93a028",
                "source": "Vessel",
                "state": "Active",
                "data": "{\"reason\":\"Skipping one or more waypoints\",\"comments\":null}",
                "created_at": "2023-06-13T11:16:19.524279"
            }
        ],
        "id": "eb9b6975-c5a4-4659-921a-2d53cc93a028",
        "imo_no": 2001193,
        "source": "Vessel",
        "category": "Voyage",
        "type": "Alarm",
        "data": "{\"id\":361253,\"imo\":2001193,\"alarmType\":\"offtrack\",\"startTs\":\"2023-05-17T07:40:00Z\",\"endTs\":\"2023-05-17T07:40:00Z\",\"alarmState\":\"active\",\"mutedUntil\":null,\"audience\":4,\"metadata\":{\"speed\":9.465777777777777,\"planId\":875717,\"planSource\":\"VoyagePlanning\",\"voyageId\":\"8iz0l-8i0ei\",\"fenceName\":\"SECA American Westcoast test\"}}",
        "created_at": "2023-06-13T10:08:53.140451"
    }
];

export const mockLoadstarNotificationResponse: VoyNotificationComposed[] = [
    {
        "state": "Active",
        "updatedAt": "2026-04-08T11:53:05.207065",
        "notification_responses": [],
        "id": "0cb47dec-0d13-4891-a596-4335783305fb",
        "imo_no": 2002101,
        "source": "Vessel",
        "category": "Voyage",
        "type": "loadstar",
        "data": "{\"fore_draft\":10.95892673981071,\"aft_draft\":9.88306721879976,\"fluid_gm\":15.624463464013841,\"solid_gm\":15.65285448816912,\"live_reefers\":0,\"max_roll_angle\":16.264331261168863,\"schedule_id\":\"a7eex-a7eey\",\"voyage_number\":\"2026156003\",\"departure_port\":\"OMSLV\",\"arrival_port\":\"INLT4\",\"saved_at\":\"2026-04-09T03:16:50Z\"}",
        "created_at": "2026-04-08T11:53:05.207065",
        "ends_at": null,
        "received_at": null
    } as VoyNotificationComposed,
];

export interface AlarmPayload {
	state: string;
}

export interface BestPracticeRoutePayload {
        available: boolean;
        selected:  boolean;
        reason:    string;
        comment:   string;
}

export interface VesselRouteSelectionNotification {
    reason: string;
    comment: string;
    navWarningNo?: string;
}

export interface VesselClfPopupNotification {
    reason: string;
    comment: string;
    correlationId?: string;
}
